import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { formatMoney } from "../../utils/format";
import { Reveal, Eyebrow } from "../../components/ui";
import {
  getExtensionTiers,
  startExtension,
  verifyExtension,
  ChatExtApiError,
} from "../../services/chatExtensionService";
import type {
  ExtendChannel,
  ExtensionTiersResponse,
  ExtensionTier,
} from "../../types/chatExtension";
import { PaymentPicker } from "./components/PaymentPicker";

type Step =
  | "loading"
  | "choosing"
  | "submitting"
  | "verifying"
  | "success"
  | "error";

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function ChatExtension() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("loading");
  const [data, setData] = useState<ExtensionTiersResponse | null>(null);
  const [selectedTier, setSelectedTier] = useState<ExtensionTier | null>(null);
  const [error, setError] = useState("");
  const [payError, setPayError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMsg, setStatusMsg] = useState("Processing…");
  const [newExpiresAt, setNewExpiresAt] = useState<string | null>(null);

  // ── Auth gate + initial load ─────────────────────────────────
  useEffect(() => {
    if (!matchId) {
      setError("Missing match ID in the URL.");
      setStep("error");
      return;
    }
    if (!localStorage.getItem("accessToken")) {
      // Send to login, then come back
      navigate(`/pay?returnTo=/extend/${matchId}`);
      return;
    }
    load();
    // eslint-disable-next-line
  }, [matchId]);

  async function load() {
    if (!matchId) return;
    setStep("loading");
    setError("");
    try {
      const res = await getExtensionTiers(matchId);
      setData(res);
      // Auto-select first tier if only one
      if (res.tiers.length === 1) setSelectedTier(res.tiers[0]);
      setStep("choosing");
    } catch (e: any) {
      if (e instanceof ChatExtApiError && e.code === "NETWORK") {
        setError("Could not reach the server. Please try again.");
      } else {
        setError(e?.message || "Could not load chat extension options.");
      }
      setStep("error");
    }
  }

  async function handleCheckout(body: {
    channel: ExtendChannel;
    msisdn?: string;
    mobileMoneyOperator?: "tnm" | "airtel";
    email?: string;
  }) {
    if (!matchId || !selectedTier) return;
    setPayError("");
    setIsSubmitting(true);

    try {
      const res = await startExtension(matchId, {
        tierCode: selectedTier.code,
        ...body,
      });

      // Card/bank hosted flow → redirect
      if (res.requiresRedirect && res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
        return;
      }

      // Mobile money → poll verify
      setStep("verifying");
      setStatusMsg("Waiting for payment confirmation…");
      pollVerify(res.reference);
    } catch (e: any) {
      setPayError(e?.message || "Could not start checkout.");
      setStep("choosing");
    } finally {
      setIsSubmitting(false);
    }
  }

  function pollVerify(reference: string) {
    let attempts = 0;
    const MAX = 30; // 30 × 4s = 2 min (server auto-expires at 120s)
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const r = await verifyExtension(matchId!, reference);
        if (r.status === "COMPLETED") {
          clearInterval(iv);
          setNewExpiresAt(r.newExpiresAt ?? null);
          setStep("success");
        } else if (r.status === "FAILED") {
          clearInterval(iv);
          setPayError("Payment failed or was cancelled.");
          setStep("choosing");
        } else if (attempts >= MAX) {
          clearInterval(iv);
          setPayError(
            "Payment timed out. If you completed it, check again shortly.",
          );
          setStep("choosing");
        }
      } catch {
        if (attempts >= MAX) {
          clearInterval(iv);
          setPayError("Could not confirm payment status.");
          setStep("choosing");
        }
      }
    }, 4000);
  }

  // ── Render ────────────────────────────────────────────────────

  return (
    <main className="bg-white px-[5%] pt-[140px] pb-20 lg:pb-[100px]">
      <div className="max-w-[640px] mx-auto">
        <Reveal>
          <Eyebrow>Chat Extension</Eyebrow>
        </Reveal>

        {step === "loading" && (
          <div className="text-center py-16">
            <div className="w-10 h-10 border-2 border-black/10 border-t-rose rounded-full animate-spin mx-auto mb-6" />
            <p className="text-[15px] text-muted">Loading your chat…</p>
          </div>
        )}

        {step === "error" && (
          <Reveal>
            <div className="mt-6 p-6 rounded-2xl border border-rose/20 bg-blush text-center">
              <p className="text-[14px] text-rose-dark mb-4">{error}</p>
              <button
                type="button"
                onClick={load}
                className="text-sm font-semibold text-black border border-black/10 px-5 py-2.5 rounded-full hover:bg-neutral-50 transition-colors"
              >
                Try again
              </button>
            </div>
          </Reveal>
        )}

        {step === "choosing" && data && (
          <Reveal delay={100}>
            <h1 className="text-3xl font-bold tracking-tight mb-3 mt-4">
              Extend your conversation
            </h1>
            <p className="text-[15px] text-muted leading-relaxed mb-6">
              {data.isExpired
                ? "This chat has expired. Extend it to keep talking."
                : `This chat stays active until ${formatDate(
                    data.currentExpiresAt,
                  )}. You can extend it now to add more time.`}
            </p>

            {/* Expiry status card */}
            <div
              className={`p-4 rounded-2xl border mb-8 ${
                data.isExpired
                  ? "border-rose/20 bg-blush"
                  : "border-black/10 bg-neutral-50"
              }`}
            >
              <p className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-1">
                {data.isExpired ? "Expired on" : "Active until"}
              </p>
              <p className="text-[15px] font-semibold text-black">
                {formatDate(data.currentExpiresAt)}
              </p>
            </div>

            {/* Tier picker */}
            <div className="space-y-3 mb-8">
              <p className="text-[11px] uppercase tracking-wider text-muted font-semibold">
                Choose extension
              </p>
              {data.tiers.map((tier) => (
                <button
                  key={tier.code}
                  type="button"
                  onClick={() => setSelectedTier(tier)}
                  className={`w-full text-left p-5 rounded-2xl border transition-all duration-200 ${
                    selectedTier?.code === tier.code
                      ? "border-rose bg-blush"
                      : "border-black/10 bg-white hover:border-black/20"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-base font-bold text-black">
                        {tier.label}
                      </p>
                      <p className="text-[13px] text-muted mt-0.5">
                        Add {tier.days} more day{tier.days !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-black">
                        {formatMoney(tier.price, tier.currency)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Payment */}
            {selectedTier && (
              <div className="p-6 rounded-2xl border border-black/10 bg-white">
                <PaymentPicker
                  amount={selectedTier.price}
                  currency={selectedTier.currency}
                  onSubmit={handleCheckout}
                  submitting={isSubmitting}
                  error={payError}
                />
              </div>
            )}

            <div className="mt-6 text-center">
              <Link
                to="/account"
                className="text-[13px] text-muted hover:text-black transition-colors no-underline"
              >
                ← Back to account
              </Link>
            </div>
          </Reveal>
        )}

        {step === "verifying" && (
          <div className="text-center py-16">
            <div className="w-10 h-10 border-2 border-black/10 border-t-rose rounded-full animate-spin mx-auto mb-6" />
            <p className="text-[15px] text-muted">{statusMsg}</p>
          </div>
        )}

        {step === "success" && (
          <Reveal>
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-full bg-rose/10 flex items-center justify-center mx-auto mb-6">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#c45a7e"
                  strokeWidth="2"
                  className="w-6 h-6"
                >
                  <polyline points="20,6 9,17 4,12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold tracking-tight mb-3">
                Chat extended
              </h1>
              <p className="text-[15px] text-muted leading-relaxed mb-2">
                Your conversation is now active
                {newExpiresAt ? " until:" : "."}
              </p>
              {newExpiresAt && (
                <p className="text-lg font-bold text-black mb-8">
                  {formatDate(newExpiresAt)}
                </p>
              )}
              <p className="text-[13px] text-muted leading-relaxed mb-8">
                Head back to the app to keep chatting.
              </p>
              <Link
                to="/account"
                className="inline-block bg-black text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-neutral-800 transition-colors no-underline"
              >
                Back to account
              </Link>
            </div>
          </Reveal>
        )}
      </div>
    </main>
  );
}
